import type { PivotMethod, Trade } from '@/mocks/technical';
import { FIB_EXTENSION, FIB_RETRACEMENT } from '@/mocks/technical';

export type PivotLevel = { key: string; label: string; price: number };
export type PivotLevels = {
  pivot: number;
  levels: PivotLevel[];
};

export function computePivotLevels(
  high: number,
  low: number,
  close: number,
  open: number,
  method: PivotMethod,
): PivotLevels {
  const range = high - low;
  let pivot: number;
  let levels: PivotLevel[] = [];

  switch (method) {
    case 'fibonacci': {
      pivot = (high + low + close) / 3;
      const r1 = pivot + 0.382 * range;
      const s1 = pivot - 0.382 * range;
      const r2 = pivot + 0.618 * range;
      const s2 = pivot - 0.618 * range;
      const r3 = pivot + range;
      const s3 = pivot - range;
      levels = [
        { key: 'r3', label: 'R3', price: r3 },
        { key: 'r2', label: 'R2', price: r2 },
        { key: 'r1', label: 'R1', price: r1 },
        { key: 's1', label: 'S1', price: s1 },
        { key: 's2', label: 'S2', price: s2 },
        { key: 's3', label: 'S3', price: s3 },
      ];
      break;
    }
    case 'camarilla': {
      pivot = (high + low + close) / 3;
      const r1 = close + (range * 1.1) / 12;
      const s1 = close - (range * 1.1) / 12;
      const r2 = close + (range * 1.1) / 6;
      const s2 = close - (range * 1.1) / 6;
      const r3 = close + (range * 1.1) / 4;
      const s3 = close - (range * 1.1) / 4;
      levels = [
        { key: 'r3', label: 'R3', price: r3 },
        { key: 'r2', label: 'R2', price: r2 },
        { key: 'r1', label: 'R1', price: r1 },
        { key: 's1', label: 'S1', price: s1 },
        { key: 's2', label: 'S2', price: s2 },
        { key: 's3', label: 'S3', price: s3 },
      ];
      break;
    }
    case 'woodie': {
      pivot = (high + low + 2 * close) / 4;
      const r1 = 2 * pivot - low;
      const s1 = 2 * pivot - high;
      const r2 = pivot + range;
      const s2 = pivot - range;
      const r3 = high + 2 * (pivot - low);
      const s3 = low - 2 * (high - pivot);
      levels = [
        { key: 'r3', label: 'R3', price: r3 },
        { key: 'r2', label: 'R2', price: r2 },
        { key: 'r1', label: 'R1', price: r1 },
        { key: 's1', label: 'S1', price: s1 },
        { key: 's2', label: 'S2', price: s2 },
        { key: 's3', label: 'S3', price: s3 },
      ];
      break;
    }
    case 'demark': {
      // Classic DeMark: pivot depends on the relationship between close and open.
      let x: number;
      if (close < open) x = high + 2 * low + close;
      else if (close > open) x = 2 * high + low + close;
      else x = high + low + 2 * close;
      pivot = x / 4;
      const r1 = x / 2 - low;
      const s1 = x / 2 - high;
      levels = [
        { key: 'r1', label: 'R1', price: r1 },
        { key: 's1', label: 'S1', price: s1 },
      ];
      break;
    }
    default: {
      // standard
      pivot = (high + low + close) / 3;
      const r1 = 2 * pivot - low;
      const s1 = 2 * pivot - high;
      const r2 = pivot + range;
      const s2 = pivot - range;
      const r3 = high + 2 * (pivot - low);
      const s3 = low - 2 * (high - pivot);
      levels = [
        { key: 'r3', label: 'R3', price: r3 },
        { key: 'r2', label: 'R2', price: r2 },
        { key: 'r1', label: 'R1', price: r1 },
        { key: 's1', label: 'S1', price: s1 },
        { key: 's2', label: 'S2', price: s2 },
        { key: 's3', label: 'S3', price: s3 },
      ];
      break;
    }
  }

  return { pivot, levels };
}

export type FibLevel = { ratio: string; price: number };

export function computeFibLevels(
  high: number,
  low: number,
  trend: 'up' | 'down',
  type: 'retracement' | 'extension',
): FibLevel[] {
  const range = high - low;
  const ratios = type === 'retracement' ? FIB_RETRACEMENT : FIB_EXTENSION;
  return ratios.map((r) => {
    let price: number;
    if (type === 'retracement') {
      price = trend === 'up' ? high - range * r : low + range * r;
    } else {
      price = trend === 'up' ? low + range * r : high - range * r;
    }
    return { ratio: `${(r * 100).toFixed(1)}%`, price };
  });
}

export type ConfluenceHit = {
  pivotKey: string;
  fibRatio: string;
  pivotPrice: number;
  fibPrice: number;
  distancePips: number;
};

export function findConfluence(
  pivotLevels: PivotLevels,
  fibLevels: FibLevel[],
  pip: number,
  thresholdPips: number,
): ConfluenceHit[] {
  const hits: ConfluenceHit[] = [];
  for (const pl of pivotLevels.levels) {
    for (const fl of fibLevels) {
      const distancePips = Math.abs(pl.price - fl.price) / pip;
      if (distancePips <= thresholdPips) {
        hits.push({
          pivotKey: pl.label,
          fibRatio: fl.ratio,
          pivotPrice: pl.price,
          fibPrice: fl.price,
          distancePips,
        });
      }
    }
  }
  return hits.sort((a, b) => a.distancePips - b.distancePips);
}

// ── Strategy data analysis ──

export type Kpis = {
  total: number;
  wins: number;
  losses: number;
  winRate: number;
  netProfit: number;
  grossProfit: number;
  grossLoss: number;
  profitFactor: number;
  maxDrawdown: number;
  avgTrade: number;
  avgWin: number;
  avgLoss: number;
  avgWinLossRatio: number;
  largestWin: number;
  largestLoss: number;
  consecutiveWins: number;
  consecutiveLosses: number;
};

export function computeKpis(trades: Trade[]): Kpis {
  const wins = trades.filter((t) => t.pnl > 0);
  const losses = trades.filter((t) => t.pnl <= 0);
  const grossProfit = wins.reduce((s, t) => s + t.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
  const netProfit = grossProfit - grossLoss;

  let peak = 0;
  let maxDd = 0;
  let cum = 0;
  for (const t of trades) {
    cum += t.pnl;
    if (cum > peak) peak = cum;
    const dd = peak - cum;
    if (dd > maxDd) maxDd = dd;
  }
  const maxDrawdownPct = peak > 0 ? (maxDd / peak) * 100 : 0;

  let cw = 0;
  let maxCw = 0;
  let cl = 0;
  let maxCl = 0;
  for (const t of trades) {
    if (t.pnl > 0) {
      cw += 1;
      cl = 0;
      if (cw > maxCw) maxCw = cw;
    } else {
      cl += 1;
      cw = 0;
      if (cl > maxCl) maxCl = cl;
    }
  }

  const avgWin = wins.length ? grossProfit / wins.length : 0;
  const avgLoss = losses.length ? grossLoss / losses.length : 0;

  return {
    total: trades.length,
    wins: wins.length,
    losses: losses.length,
    winRate: trades.length ? (wins.length / trades.length) * 100 : 0,
    netProfit,
    grossProfit,
    grossLoss,
    profitFactor: grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0,
    maxDrawdown: maxDrawdownPct,
    avgTrade: trades.length ? netProfit / trades.length : 0,
    avgWin,
    avgLoss,
    avgWinLossRatio: avgLoss > 0 ? avgWin / avgLoss : 0,
    largestWin: wins.length ? Math.max(...wins.map((t) => t.pnl)) : 0,
    largestLoss: losses.length ? Math.min(...losses.map((t) => t.pnl)) : 0,
    consecutiveWins: maxCw,
    consecutiveLosses: maxCl,
  };
}

export type EquityPoint = {
  index: number;
  date: string;
  cumulative: number;
  balance: number;
  tradeCount: number;
};

export function buildEquityCurve(trades: Trade[], startBalance = 10000): EquityPoint[] {
  let cum = 0;
  return trades.map((t, i) => {
    cum += t.pnl;
    return {
      index: i + 1,
      date: t.date,
      cumulative: cum,
      balance: startBalance + cum,
      tradeCount: i + 1,
    };
  });
}

export type DrawdownPoint = { index: number; date: string; drawdown: number };

export function buildDrawdownCurve(trades: Trade[]): DrawdownPoint[] {
  let peak = 0;
  let cum = 0;
  return trades.map((t, i) => {
    cum += t.pnl;
    if (cum > peak) peak = cum;
    const dd = peak - cum;
    return { index: i + 1, date: t.date, drawdown: dd };
  });
}

const WEEKDAYS = ['CN', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

export type WeekdayStat = {
  label: string;
  count: number;
  wins: number;
  winRate: number;
  netPnl: number;
  avgTrade: number;
};

export function byWeekday(trades: Trade[]): WeekdayStat[] {
  const days = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6'];
  return days.map((label) => {
    const subset = trades.filter((t) => {
      const d = new Date(t.date);
      return WEEKDAYS[d.getDay()] === label;
    });
    const wins = subset.filter((t) => t.pnl > 0).length;
    const net = subset.reduce((s, t) => s + t.pnl, 0);
    return {
      label,
      count: subset.length,
      wins,
      winRate: subset.length ? (wins / subset.length) * 100 : 0,
      netPnl: net,
      avgTrade: subset.length ? net / subset.length : 0,
    };
  });
}

export type SessionStat = {
  label: string;
  count: number;
  winRate: number;
  netPnl: number;
  avgTrade: number;
};

export function bySession(trades: Trade[]): SessionStat[] {
  const sessions = ['Asia', 'London', 'New York'];
  return sessions.map((label) => {
    const subset = trades.filter((t) => t.session === label);
    const wins = subset.filter((t) => t.pnl > 0).length;
    const net = subset.reduce((s, t) => s + t.pnl, 0);
    return {
      label,
      count: subset.length,
      winRate: subset.length ? (wins / subset.length) * 100 : 0,
      netPnl: net,
      avgTrade: subset.length ? net / subset.length : 0,
    };
  });
}

export type DirectionStat = {
  label: string;
  count: number;
  winRate: number;
  netPnl: number;
  avgTrade: number;
};

export function byDirection(trades: Trade[]): DirectionStat[] {
  const dirs: ('long' | 'short')[] = ['long', 'short'];
  return dirs.map((dir) => {
    const subset = trades.filter((t) => t.dir === dir);
    const wins = subset.filter((t) => t.pnl > 0).length;
    const net = subset.reduce((s, t) => s + t.pnl, 0);
    return {
      label: dir === 'long' ? 'Long (Mua)' : 'Short (Bán)',
      count: subset.length,
      winRate: subset.length ? (wins / subset.length) * 100 : 0,
      netPnl: net,
      avgTrade: subset.length ? net / subset.length : 0,
    };
  });
}

export function splitPeriod(trades: Trade[]): { a: Trade[]; b: Trade[] } {
  const half = Math.floor(trades.length / 2);
  return { a: trades.slice(0, half), b: trades.slice(half) };
}